import React, { useEffect } from 'react';
import { NavLink, Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutGrid, Zap, FileText, BookOpen, Settings as SettingsIcon,
  Receipt, Users, LogOut, ArrowUpRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { isAuthenticated, logout, getUser } from '../utils/api';

const nav = [
  { label: 'Overview', to: '/dashboard', icon: 'LayoutGrid', exact: true },
  { label: 'Run Check', to: '/dashboard/run', icon: 'Zap' },
  { label: 'History', to: '/dashboard/history', icon: 'FileText' },
  { label: 'Frameworks', to: '/dashboard/frameworks', icon: 'BookOpen' },
  { label: 'Settings', to: '/dashboard/settings', icon: 'Settings' },
  { label: 'My Plan', to: '/dashboard/plan', icon: 'Receipt' },
];

const adminNav = [
  { label: 'Admin', to: '/dashboard/admin', icon: 'Users' },
];

const IconMap = {
  LayoutGrid, Zap, FileText, BookOpen, Settings: SettingsIcon, Receipt, Users,
};

export default function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
    }
  }, [navigate]);

  const signOut = () => {
    logout();
    toast.success('Signed out securely.');
    navigate('/login');
  };

  const titles = {
    '/dashboard': 'Compliance Overview',
    '/dashboard/run': 'Compliance Check',
    '/dashboard/history': 'Check History',
    '/dashboard/frameworks': 'Framework Registry',
    '/dashboard/settings': 'Settings',
    '/dashboard/plan': 'My Plan',
    '/dashboard/admin': 'Admin',
  };
  const current = titles[location.pathname] || 'Dashboard';

  if (!isAuthenticated()) return null;

  return (
    <div className="min-h-screen bg-[#FAFAF5] flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-[260px] shrink-0 bg-[#0B1F2D] text-[#FAFAF5] flex-col sticky top-0 h-screen">
        {/* Brand */}
        <Link to="/dashboard" className="flex items-center gap-2.5 px-6 h-[72px] border-b border-white/10">
          <img src="/esgine-logo.png" alt="ESGine" className="w-9 h-9 object-contain" />
        <div className="font-display text-[20px] leading-none tracking-tight">
          ESGine<sup style={{fontSize:'8px', verticalAlign:'middle', marginLeft:'2px'}}>®</sup>
        </div>
        </Link>

        {/* ESG-as-Code pill */}
        <div className="px-4 pt-5">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.04] text-[10.5px] tracking-[0.12em] uppercase text-[#7BC2B0]">
            <span className="relative w-1.5 h-1.5">
              <span className="absolute inset-0 rounded-full bg-[#7BC2B0]" />
              <span className="live-dot" />
            </span>
            ESG-as-Code® Active
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 pt-6 space-y-0.5 overflow-y-auto">
          {nav.map((item) => {
            const Ico = IconMap[item.icon];
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] font-medium transition-colors ${
                    isActive
                      ? 'bg-white/10 text-[#FAFAF5]'
                      : 'text-[#FAFAF5]/65 hover:bg-white/[0.06] hover:text-[#FAFAF5]'
                  }`
                }
              >
                <Ico className="w-4.5 h-4.5 shrink-0" />
                {item.label}
              </NavLink>
            );
          })}

          {user?.role === 'admin' && (
            <>
              <div className="px-3 pt-5 pb-1 text-[10px] uppercase tracking-[0.14em] text-[#FAFAF5]/35">Admin</div>
              {adminNav.map((item) => {
                const Ico = IconMap[item.icon];
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] font-medium transition-colors ${
                        isActive
                          ? 'bg-white/10 text-[#FAFAF5]'
                          : 'text-[#FAFAF5]/65 hover:bg-white/[0.06] hover:text-[#FAFAF5]'
                      }`
                    }
                  >
                    <Ico className="w-4.5 h-4.5 shrink-0" />
                    {item.label}
                  </NavLink>
                );
              })}
            </>
          )}
        </nav>

        {/* User footer */}
        <div className="border-t border-white/10 px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <div className="text-[13px] font-medium text-[#FAFAF5] truncate">
                {user ? `${user.first_name} ${user.last_name}` : 'Client'}
              </div>
              <div className="text-[11px] text-[#FAFAF5]/50 truncate">{user?.email}</div>
            </div>
            <button
              onClick={signOut}
              className="ml-3 w-8 h-8 rounded-lg bg-white/[0.06] hover:bg-white/10 flex items-center justify-center text-[#FAFAF5]/65 hover:text-[#FAFAF5] transition-colors"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-[72px] border-b border-[#E8E5DC] bg-white flex items-center justify-between px-6 lg:px-8 sticky top-0 z-30">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#0B1F2D]/50">ESGine® Dashboard</div>
            <div className="text-[15px] font-medium text-[#0B1F2D]">{current}</div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-1.5 h-9 px-4 rounded-full border border-[#E8E5DC] text-[12.5px] text-[#0B1F2D]/70 hover:text-[#0B1F2D] hover:border-[#0B1F2D] transition-colors"
            >
              esgine.io <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
